import React from "react";
import { cn } from "../../utils/cn";

type TextVariant =
  | "h1"
  | "h2"
  | "h3"
  | "body"
  | "bodySmall"
  | "caption"
  | "label"
  | "micro";

type TextColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "muted"
  | "white"
  | "blue"
  | "green"
  | "red"
  | "amber";

type TextWeight = "normal" | "medium" | "semibold" | "bold";

type TextAlign = "left" | "center" | "right";

type TextTransform = "uppercase" | "capitalize" | "none";

type LetterSpacing = "tight" | "normal" | "wide";

type PolymorphicElement =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "p"
  | "span"
  | "div"
  | "label";

interface TextProps {
  variant?: TextVariant;
  color?: TextColor;
  weight?: TextWeight;
  align?: TextAlign;
  transform?: TextTransform;
  letterSpacing?: LetterSpacing;
  className?: string;
  as?: PolymorphicElement;
  children: React.ReactNode;
}

const variantStyles: Record<TextVariant, string> = {
  // Headings - compact but clear hierarchy
  h1: "text-xl md:text-2xl",
  h2: "text-lg md:text-xl",
  h3: "text-base md:text-lg",

  // Body text - efficient, iOS-safe
  body: "text-base",
  bodySmall: "text-base md:text-sm",

  // Compact labels - 16px mobile (iOS-safe), 13px desktop for density
  caption: "text-base md:text-[13px]",
  label: "text-base md:text-[13px]",

  // Micro text - 16px mobile, 12px desktop (for timestamps, metadata)
  micro: "text-base md:text-xs",
};

const defaultElements: Record<TextVariant, PolymorphicElement> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  body: "p",
  bodySmall: "p",
  caption: "span",
  label: "label",
  micro: "span",
};

const defaultWeights: Record<TextVariant, TextWeight> = {
  h1: "bold",
  h2: "semibold",
  h3: "semibold",
  body: "normal",
  bodySmall: "normal",
  caption: "normal",
  label: "medium",
  micro: "normal",
};

const colorStyles: Record<TextColor, string> = {
  primary: "text-gray-900",
  secondary: "text-gray-700",
  tertiary: "text-gray-600",
  muted: "text-gray-500",
  white: "text-white",
  blue: "text-blue-600",
  green: "text-green-600",
  red: "text-red-600",
  amber: "text-amber-600",
};

const weightStyles: Record<TextWeight, string> = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

const alignStyles: Record<TextAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const transformStyles: Record<TextTransform, string> = {
  uppercase: "uppercase",
  capitalize: "capitalize",
  none: "",
};

const letterSpacingStyles: Record<LetterSpacing, string> = {
  tight: "tracking-tight",
  normal: "tracking-normal",
  wide: "tracking-wide",
};

export const Text: React.FC<TextProps> = ({
  variant = "body",
  color = "primary",
  weight,
  align,
  transform,
  letterSpacing,
  className,
  as,
  children,
}) => {
  const Element = as || defaultElements[variant];
  const effectiveWeight = weight || defaultWeights[variant];

  return (
    <Element
      className={cn(
        // Variant (size) styles
        variantStyles[variant],

        // Color
        colorStyles[color],

        // Weight
        weightStyles[effectiveWeight],

        // Alignment
        align && alignStyles[align],

        // Transform
        transform && transformStyles[transform],

        // Letter spacing
        letterSpacing && letterSpacingStyles[letterSpacing],

        // Custom classes
        className,
      )}
    >
      {children}
    </Element>
  );
};
